import React, {useEffect, useMemo, useState} from 'react';
import {Box, Button, Card, CardContent, CardHeader, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography} from '@mui/material';
import {DataGrid} from '@mui/x-data-grid';
import {useNavigate} from 'react-router-dom';
import {useAuth} from '../auth-context';
import {useClient} from '../core/client';

const Impersonate = () => {
    const { user, login } = useAuth();
    const client = useClient();
    const navigate = useNavigate();
    const [identifier, setIdentifier] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [backfillDialogOpen, setBackfillDialogOpen] = useState(false);
    const [backfillDaysBack, setBackfillDaysBack] = useState('');
    const [backfillLoading, setBackfillLoading] = useState(false);
    const [backfillError, setBackfillError] = useState('');
    const [backfillResult, setBackfillResult] = useState(null);
    const [sendReminderLoading, setSendReminderLoading] = useState(false);
    const [sendReminderError, setSendReminderError] = useState('');
    const [sendReminderResult, setSendReminderResult] = useState(null);
    const [customersLoading, setCustomersLoading] = useState(false);
    const [customersError, setCustomersError] = useState('');
    const [customerRows, setCustomerRows] = useState([]);

    const isAdmin = useMemo(
        () => (user?.role || '').toLowerCase() === 'admin',
        [user?.role]
    );

    useEffect(() => {
        if (!isAdmin) return;
        let mounted = true;
        const fetchCustomers = async () => {
            setCustomersLoading(true);
            setCustomersError('');
            try {
                const { data } = await client.get('/users');
                const users = Array.isArray(data) ? data : [];
                const groupedByCustomer = new Map();

                for (const record of users) {
                    const customer = record?.customer && typeof record.customer === 'object' ? record.customer : null;
                    const customerId = customer?._id || record?.customer || `unassigned-${record?._id || record?.id}`;
                    const customerName = customer?.name || 'Unassigned Customer';
                    const userId = record?._id || record?.id || '';
                    const email = record?.email || '';
                    const role = record?.role || 'user';

                    if (!groupedByCustomer.has(customerId)) {
                        groupedByCustomer.set(customerId, {
                            id: customerId,
                            customerName,
                            userId,
                            email,
                            role,
                            usersCount: 1,
                        });
                        continue;
                    }

                    const existing = groupedByCustomer.get(customerId);
                    existing.usersCount += 1;
                    if (existing.role === 'admin' && role !== 'admin') {
                        existing.userId = userId;
                        existing.email = email;
                        existing.role = role;
                    }
                }

                if (mounted) {
                    setCustomerRows(Array.from(groupedByCustomer.values()));
                }
            } catch (err) {
                if (mounted) {
                    setCustomersError('Unable to load customers for impersonation.');
                }
            } finally {
                if (mounted) {
                    setCustomersLoading(false);
                }
            }
        };
        fetchCustomers();
        return () => {
            mounted = false;
        };
    }, [client, isAdmin]);

    const handleImpersonateByUserId = async (userId) => {
        if (!userId) return;
        setLoading(true);
        setError('');
        try {
            const { data } = await client.post('/auth/impersonate', { userId });
            await login(data);
            navigate('/');
        } catch (err) {
            setError('Unable to impersonate that user. Check the identifier and try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        const trimmed = identifier.trim();
        if (!trimmed) return;
        setLoading(true);
        setError('');
        try {
            const payload = trimmed.includes('@') ? { email: trimmed } : { userId: trimmed };
            const { data } = await client.post('/auth/impersonate', payload);
            await login(data);
            navigate('/');
        } catch (err) {
            setError('Unable to impersonate that user. Check the identifier and try again.');
        } finally {
            setLoading(false);
        }
    };

    const customerColumns = [
        { field: 'customerName', headerName: 'Customer', flex: 1, minWidth: 220 },
        { field: 'email', headerName: 'Impersonation User', flex: 1, minWidth: 240 },
        { field: 'role', headerName: 'Role', width: 120 },
        { field: 'usersCount', headerName: 'Users', width: 100 },
        {
            field: 'actions',
            headerName: 'Action',
            width: 170,
            sortable: false,
            filterable: false,
            renderCell: (params) => (
                <Button
                    variant="contained"
                    size="small"
                    onClick={() => handleImpersonateByUserId(params.row.userId)}
                    disabled={loading || !params.row.userId}
                >
                    Impersonate
                </Button>
            ),
        },
    ];

    const handleOpenBackfill = () => {
        setBackfillError('');
        setBackfillResult(null);
        setBackfillDialogOpen(true);
    };

    const handleCloseBackfill = () => {
        if (backfillLoading) return;
        setBackfillDialogOpen(false);
    };

    const handleRunBackfill = async () => {
        setBackfillLoading(true);
        setBackfillError('');
        setBackfillResult(null);
        try {
            const daysBackValue = backfillDaysBack.trim();
            const daysBackNumber = daysBackValue ? Number(daysBackValue) : undefined;
            const payload = {};
            if (Number.isFinite(daysBackNumber) && daysBackNumber > 0) {
                payload.daysBack = daysBackNumber;
            }
            const { data } = await client.post('/admin/backfill-missed-checkins', payload);
            setBackfillResult(data);
        } catch (err) {
            setBackfillError('Unable to run backfill. Please try again.');
        } finally {
            setBackfillLoading(false);
        }
    };

    const handleSendRemindersNow = async () => {
        setSendReminderLoading(true);
        setSendReminderError('');
        setSendReminderResult(null);
        try {
            const { data } = await client.post('/admin/send-checkin-reminders-now');
            setSendReminderResult(data);
        } catch (err) {
            setSendReminderError('Unable to send reminders right now. Please try again.');
        } finally {
            setSendReminderLoading(false);
        }
    };

    if (!isAdmin) {
        return (
            <Box sx={{ p: { xs: 2, md: 3 } }}>
                <Typography variant="h5">Access denied</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    You need an admin account to use this page.
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ p: { xs: 2, md: 3 } }}>
            <Card sx={{ mb: 3 }}>
                <CardHeader title="Customers" />
                <CardContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Select a customer row to impersonate the associated user account.
                    </Typography>
                    <Box sx={{ width: '100%', height: 420 }}>
                        <DataGrid
                            rows={customerRows}
                            columns={customerColumns}
                            loading={customersLoading}
                            disableRowSelectionOnClick
                            pageSizeOptions={[5, 10, 25]}
                            initialState={{
                                pagination: {
                                    paginationModel: { pageSize: 10, page: 0 },
                                },
                                sorting: {
                                    sortModel: [{ field: 'customerName', sort: 'asc' }],
                                },
                            }}
                        />
                    </Box>
                    {customersError && (
                        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                            {customersError}
                        </Typography>
                    )}
                </CardContent>
            </Card>

            <Card sx={{ maxWidth: 520, mb: 3 }}>
                <CardHeader title="Impersonate User" />
                <CardContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Enter a user email or user ID to log in as that user.
                    </Typography>
                    <Box component="form" onSubmit={handleSubmit}>
                        <TextField
                            fullWidth
                            label="User email or ID"
                            value={identifier}
                            onChange={(e) => setIdentifier(e.target.value)}
                            disabled={loading}
                            autoComplete="off"
                        />
                        {error && (
                            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                                {error}
                            </Typography>
                        )}
                        <Button
                            type="submit"
                            variant="contained"
                            sx={{ mt: 2 }}
                            disabled={loading || !identifier.trim()}
                        >
                            {loading ? 'Impersonating...' : 'Impersonate'}
                        </Button>
                    </Box>
                </CardContent>
            </Card>
            <Card sx={{ maxWidth: 520 }}>
                <CardHeader title="Admin Tools" />
                <CardContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Run one-time maintenance tasks for the platform.
                    </Typography>
                    <Button variant="contained" onClick={handleOpenBackfill} sx={{ mr: 1, mb: 1 }}>
                        Backfill Missed Check-Ins
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSendRemindersNow}
                        disabled={sendReminderLoading}
                        sx={{ mb: 1 }}
                    >
                        {sendReminderLoading ? 'Sending...' : 'Send Check-In Reminders Now'}
                    </Button>
                    {sendReminderResult && (
                        <Typography variant="body2" sx={{ mt: 1 }}>
                            Sent {sendReminderResult.sent} reminder(s). Evaluated {sendReminderResult.defendantsEvaluated} defendant(s).
                        </Typography>
                    )}
                    {sendReminderError && (
                        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                            {sendReminderError}
                        </Typography>
                    )}
                </CardContent>
            </Card>
            <Dialog open={backfillDialogOpen} onClose={handleCloseBackfill} maxWidth="sm" fullWidth>
                <DialogTitle>Backfill Missed Check-Ins</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        This will create missed check-in records for past scheduled dates without a check-in.
                    </Typography>
                    <TextField
                        fullWidth
                        label="Days back (optional)"
                        placeholder="e.g. 30"
                        value={backfillDaysBack}
                        onChange={(e) => setBackfillDaysBack(e.target.value)}
                        disabled={backfillLoading}
                        autoComplete="off"
                    />
                    {backfillResult && (
                        <Typography variant="body2" sx={{ mt: 2 }}>
                            Created {backfillResult.created} missed check-ins across {backfillResult.schedulesTouched} schedules.
                        </Typography>
                    )}
                    {backfillError && (
                        <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                            {backfillError}
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseBackfill} disabled={backfillLoading}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleRunBackfill}
                        disabled={backfillLoading}
                    >
                        {backfillLoading ? 'Running...' : 'Run Backfill'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Impersonate;
